// Public score-sheet self-service API helpers. This app has no generated
// client and no auth — all three endpoints are public. Base URL comes from
// VITE_REPORT_API_BASE_URL (report-api, local port 3003).
const BASE_URL = import.meta.env.VITE_REPORT_API_BASE_URL;

// #1637 — the public scoreboard talks to score-api, not report-api. Net-new
// base URL for this app (design docs/score/scoreboard-design.md §4.1); same
// plain-fetch, no-auth convention as everything else in this file.
const SCORE_BASE_URL = import.meta.env.VITE_SCORE_API_BASE_URL;

// GET config (#1957 — data-driven show list, replaces the pinned single-show
// shape): { configured, scoreSiteUrl, shows: [{id, name, startDate, endDate,
// location, isCurrent}], defaultShowIds }. `shows` is already sorted
// reverse-chronological by report-api; `defaultShowIds` names whichever
// show(s) are currently running, or the most recent one(s) when none is.
export async function fetchConfig() {
  const res = await fetch(`${BASE_URL}/public/score-sheets/config`);
  if (!res.ok) {
    throw new Error(`config request failed (${res.status})`);
  }
  return res.json();
}

// POST request-by-email+CNO+showId. Returns { status, body } so callers can
// branch on the HTTP status (202 found / 200 not-found / 400 validation).
// #1957 — showId is now required: the visitor's dropdown pick (or the
// config's own default), never a server-pinned show.
export async function requestScoreSheet({ email, cno, showId }) {
  const res = await fetch(`${BASE_URL}/public/score-sheets/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, cno, showId }),
  });
  let body = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  return { status: res.status, body };
}

// GET a generated bundle by token. Returns { status, body }; 404 => unknown token.
export async function fetchScoreSheet(token) {
  const res = await fetch(
    `${BASE_URL}/public/score-sheets/${encodeURIComponent(token)}`
  );
  let body = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  return { status: res.status, body };
}

// Card #971 — click-time presign pass-through. SheetsPage's View/Download
// buttons never carry a raw S3 URL; instead they link to this app's own
// /open/:requestId/:rideId/:variant interstitial, which calls this and
// redirects to whatever `url` it gets back. Returns { status, body } so the
// interstitial can branch on: 200 { url } ready, 202 { status: "generating" }
// still rendering, 404 unknown / withheld, 410 { status: "unavailable" }
// gone-and-past-the-regen-window.
// `cache: "no-store"` is load-bearing, not cosmetic: this same URL is polled
// repeatedly (the "generating" state) and revisited across a rider's clicks,
// and several of the possible responses (404, 410) are heuristically
// cacheable per RFC 9111 with no explicit Cache-Control from report-api. A
// browser-cached 410/404 would silently keep serving a stale verdict even
// after the artifact exists — defeating the whole point of minting fresh at
// click time.
export async function fetchOpenUrl({ requestId, rideId, variant }) {
  const res = await fetch(
    `${BASE_URL}/public/sheets/${encodeURIComponent(requestId)}/${encodeURIComponent(rideId)}/${encodeURIComponent(variant)}/url`,
    { cache: "no-store" }
  );
  let body = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  return { status: res.status, body };
}

// #1637 — GET the composed public scoreboard envelope (design §3.1, built by
// card #1636's apps/score-api/src/handlers/scoreboard-public.js): view, mode,
// theme, sponsor, payload, serverTime, in one poll-friendly read. `slug`
// omitted resolves the show's default view. `cache: "no-store"` — this is
// polled every 5s and must never serve a browser-cached stale envelope.
//
// Throws on a non-2xx response or a malformed body so the caller's poll loop
// (useScoreboardPoll) can treat it as a failed poll and keep the last good
// payload on screen, per design §4.2 — never resolves to a half-built object.
//
// Card #2493: `previewModeId` asks for the payload of a mode that is NOT live,
// composed server-side exactly as if it were (the remote's local preview). It is
// a read; nothing on any live screen changes.
export async function fetchScoreboard({ showId, year, slug, previewModeId = null }) {
  const segments = [showId, year, slug].filter((s) => s != null && s !== "");
  const query = previewModeId ? `?previewModeId=${encodeURIComponent(previewModeId)}` : "";
  const path = `/public/scoreboard/${segments.map(encodeURIComponent).join("/")}${query}`;
  const res = await fetch(`${SCORE_BASE_URL}${path}`, { cache: "no-store" });
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok || !body || body.status !== "success") {
    const message = body?.data?.message || `scoreboard request failed (${res.status})`;
    throw new Error(message);
  }
  return body.data;
}

// #1641 — GET the public class-results read (design §3.1/§6.3, built by
// apps/score-api/src/handlers/scoreboard-public.js's getPublicClassResults):
// the QR-code target, `/public/scoreboard/:showId/class/:classId/results`.
// Confirmed/final rows only, same exposure rules as the board. `year` is
// NOT part of this path (unlike fetchScoreboard's) — the endpoint does no
// year check at all, so ClassResultsPage's `/results/:showId/:year/:classNumber`
// (#1854 — renamed from :classId) route param is FE-side/cosmetic only and
// never sent here.
//
// Same "throw on non-2xx or malformed body" contract as fetchScoreboard, but
// the thrown Error also carries `.code` (one of show_not_found/
// view_not_found/class_not_found on a 404) and `.status` so the page can
// render every 404 the same generic "not available" way (per the card: a
// view_not_found on a real show/class is an exposure gate, not "does not
// exist", so the FE must not claim non-existence from the code alone).
// `cache: "no-store"` — this is a one-shot QR-scan read, not the 5s poll,
// but a rider could still reload/rescan and must never get a browser-cached
// stale verdict.
// #1854 — GET the public showId+classNumber -> classId resolver (built by
// apps/score-api/src/handlers/results.js's resolveClassByNumber):
// `/public/scoreboard/:showId/class-number/:classNumber`. The board's QR now
// encodes a short, human-readable class NUMBER rather than the internal
// fvd: classId, so ClassResultsPage calls this FIRST and only then calls
// fetchClassResults below with the resolved classId — the classId-keyed
// endpoint itself is unchanged. Same "throw on non-2xx or malformed body,
// carry .code/.status" contract as fetchClassResults, so both can share one
// error-rendering path in the page (every 404 — show_not_found,
// view_not_found, class_not_found — reads the same generic "not available"
// way; a non-public show must not be distinguishable from a nonexistent
// one). `cache: "no-store"` for the same one-shot-QR-scan reasoning as
// fetchClassResults.
export async function resolveClassId({ showId, classNumber }) {
  const path = `/public/scoreboard/${encodeURIComponent(showId)}/class-number/${encodeURIComponent(classNumber)}`;
  const res = await fetch(`${SCORE_BASE_URL}${path}`, { cache: "no-store" });
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok || !body || body.status !== "success") {
    const message = body?.data?.message || `class resolve request failed (${res.status})`;
    const error = new Error(message);
    error.code = body?.code || null;
    error.status = res.status;
    throw error;
  }
  return body.data;
}

export async function fetchClassResults({ showId, classId }) {
  const path = `/public/scoreboard/${encodeURIComponent(showId)}/class/${encodeURIComponent(classId)}/results`;
  const res = await fetch(`${SCORE_BASE_URL}${path}`, { cache: "no-store" });
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok || !body || body.status !== "success") {
    const message = body?.data?.message || `class results request failed (${res.status})`;
    const error = new Error(message);
    error.code = body?.code || null;
    error.status = res.status;
    throw error;
  }
  return body.data;
}
